import { Endpoints } from "../helpers/Endpoints";
import * as APIModels from "../models";
import { authenticationService } from "../services/authentication.service";

export const fileService = {
    get,
    upload,
    uploadError,
    mkdir,
    thirdParty,
};

/**
 * Retrieves a document from an S3 bucket, by returning the url.
 *
 * @param {Object} params - Parameters for the document retrieval.
 * @param {string} params.submissionId - The submission ID.
 * @param {string} params.documentId - The document ID.
 * @param {string | null} params.source - Optional source information (can be null).
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response.
 *                              This will be used elsewhere to retrieve the document
 *                              as a url to the blob.
 */
function get({
    submissionId,
    documentId,
    source,
}: {
    submissionId: string;
    documentId: string;
    source: string | null;
}): Promise<Response> {
    const currentUser = authenticationService.currentUserValue;
    console.log("File Service URL", Endpoints.FileManager);
    if (currentUser != null) {
        const requestOptions = {
            method: "GET",
            //  mode: "cors" as RequestMode,
            headers: { Authorization: `${currentUser.authToken}` },
        };

        console.log(
            `${
                Endpoints.FileManager
            }?key=${submissionId}/${documentId}&source=${source ?? ""}`
        );

        return fetch(
            `${
                Endpoints.FileManager
            }?key=${submissionId}/${documentId}&source=${source ?? ""}`,
            requestOptions
        );
    }

    return fetch(`${Endpoints.FileManager}`);
}

/**
 * Uploads a document to an S3 bucket using a pre-signed URL.
 *
 * @param {Object} params - Parameters for the document upload.
 * @param {any} params.file - The file(s) to be uploaded. Can be a single file or an array of files.
 * @param {string} params.filename - The name of the file to be uploaded.
 * @param {string} params.uuid - The optional UUID of the document being uploaded.
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response.
 */
async function upload(
    params: {
        /**  */
        fileServiceUrl: string;
        file: any;
        filename: string;
        uuid?: string;
    } = { fileServiceUrl: Endpoints.FileManager } as any
): Promise<Response> {
    const currentUser = authenticationService.currentUserValue;
    if (currentUser != null) {
        console.log("UUID", currentUser.userID ?? params.uuid);
        // The file manager answers { upload_url }. When it fails (its own error body,
        // a gateway error such as throttling, or a 200 with an empty link) there is
        // no URL, and fetch("") would PUT the file to the current page, which the
        // portal's host rejects with 405. Ask again a couple of times when that's
        // worth it, then hand the failure back as a non-ok Response instead.
        const requestLink = async () => {
            const resp = await uploadUrl({
                fileServiceUrl: params.fileServiceUrl,
                uid: currentUser.userID ?? params.uuid,
                key: params.filename,
            });
            return { resp, data: await resp.json().catch(() => ({})) };
        };
        const linkFailed = (resp: Response, data: any) =>
            !resp.ok || !data.upload_url;
        const worthRetrying = (resp: Response) =>
            resp.ok || resp.status === 429 || resp.status >= 500;

        let { resp: urlResponse, data: UrlData } = await requestLink();
        for (let attempt = 1; attempt <= 2; attempt++) {
            if (
                !linkFailed(urlResponse, UrlData) ||
                !worthRetrying(urlResponse)
            )
                break;
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
            ({ resp: urlResponse, data: UrlData } = await requestLink());
        }

        if (linkFailed(urlResponse, UrlData)) {
            const reason =
                UrlData.error ||
                UrlData.message ||
                (urlResponse.ok
                    ? "the file manager returned no upload link"
                    : `the file manager returned ${urlResponse.status}`);
            return new Response(JSON.stringify({ ...UrlData, error: reason }), {
                status: urlResponse.ok ? 502 : urlResponse.status,
                statusText: "Upload link unavailable",
                headers: { "Content-Type": "application/json" },
            });
        }

        let data = null;
        data = new FormData();
        const multipleFiles =
            Object.prototype.toString.call(params["file"]) === "[object Array]";
        if (params["file"]) {
            if (multipleFiles) {
                for (const item of params["file"]) {
                    data.append("file", item as any);
                }
            }
        }

        let contentType = "application/octet-stream";
        if (params.filename.toLowerCase().indexOf(".pdf") > 0)
            contentType = "application/pdf";

        const requestOptions = {
            method: "PUT",
            //mode: "no-cors" as RequestMode,
            headers: {
                "Content-Type": contentType,
            },

            body: multipleFiles ? data : params["file"],
        };

        return fetch(
            UrlData.upload_url,
            // `https://xx0yydmcx3.execute-api.us-east-1.amazonaws.com/Prod?uid=${currentUser.userID}&key=${params.filename}`,
            requestOptions
        );
    }

    return fetch(`${params.fileServiceUrl}`, { method: "GET" });
}

/**
 * A readable reason for a failed upload Response: the file manager's error text
 * when it sent one, otherwise the HTTP status.
 *
 * @param {Response} resp - A non-ok Response returned by `upload`.
 * @returns {Promise<string>} The reason the upload failed.
 */
async function uploadError(resp: Response): Promise<string> {
    const body = await resp.json().catch(() => ({}));
    return (
        body.error || body.message || `${resp.status} ${resp.statusText}`.trim()
    );
}

/**
 * Retrieves a pre-signed URL for uploading a document to an S3 bucket.
 *
 * @param {Object} params - Parameters for generating the upload URL.
 * @param {string} params.uid - The user ID associated with the upload.
 * @param {string} params.key - The key or filename for the document being uploaded.
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response containing the pre-signed URL.
 */
function uploadUrl({
    uid,
    key,
    fileServiceUrl,
}: {
    uid: string;
    key: string;
    fileServiceUrl: string;
}) {
    const currentUser = authenticationService.currentUserValue;
    console.log("File Service URL", fileServiceUrl);
    if (currentUser != null) {
        const requestOptions = {
            method: "POST",
            mode: "cors" as RequestMode,
            headers: {
                Authorization: `${currentUser.authToken}`,
                "Content-Type": "application/json",
            },
        };

        return fetch(`${fileServiceUrl}?uid=${uid}&key=${key}`, requestOptions);
    }

    return fetch(`${fileServiceUrl}`);
}

/**
 * Uploads documents to the eFileMadeEasy Queue using a third-party API.
 *
 * @param {Object} params - Parameters for uploading documents to the queue.
 * @param {string} params.url - The URL of the third-party API for uploading documents.
 * @param {string} params.attorneyId - The attorney ID associated with the documents.
 * @param {string} params.filename - The name of the document being uploaded.
 * @param {any} params.file - The file or an array of files to be uploaded.
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response.
 */
function thirdParty(
    params: {
        fileServiceUrl: string;
        url: string;
        attorneyId: string;
        filename: string;
        file: any;
    } = {} as any
): Promise<Response> {
    const currentUser = authenticationService.currentUserValue;
    if (currentUser != null) {
        const data = new FormData();

        if (params.file) {
            if (Array.isArray(params.file)) {
                for (const item of params.file) {
                    data.append("Package", item);
                }
            } else {
                data.append("Package", params.file);
            }
        }

        data.append("AttorneyId", params.attorneyId);

        const requestOptions: RequestInit = {
            method: "POST",
            mode: "cors",
            headers: {
                Authorization: `Bearer ${currentUser.authToken}`,
                "X-User-AccessToken": currentUser.accessToken,
                "X-User-RefreshToken": currentUser.refreshToken,
            },
            body: data,
        };

        console.log(requestOptions);

        return fetch(params.url, requestOptions);
    }

    return fetch(params.fileServiceUrl, { method: "GET" });
}

/**
 * Creates an empty folder in an S3 bucket using a pre-signed URL.
 *
 * @param {Object} params - Parameters for creating the folder.
 * @param {string} params.fileServiceUrl - The file service URL for generating a pre-signed upload URL.
 * @param {string} params.folderPath - The path to the folder being created (without leading directories).
 * @returns {Promise<Response>} A Promise that resolves to the HTTP Response containing the result of the operation.
 */
async function mkdir(
    params: {
        fileServiceUrl: string;
        folderPath: string;
    } = { fileServiceUrl: Endpoints.FileManager } as any
): Promise<Response> {
    const currentUser = authenticationService.currentUserValue;

    if (currentUser != null) {
        console.log("UUID", currentUser.userID ?? "");
        const urlResponse = await uploadUrl({
            fileServiceUrl: params.fileServiceUrl,
            uid: currentUser.userID ?? "",
            key: `${params.folderPath}/`,
        });

        return fetch(urlResponse.url);
    }

    return fetch(`${params.fileServiceUrl}`, { method: "GET" });
}
